import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useJoiningBonus } from './JoiningBonusContext';
import { useAuth } from './AuthContext';
import { toast } from '@/hooks/use-toast';
import { useVerificationStatusQuery } from '@/hooks/queries/useVerificationStatusQuery';
import { 
  setGlobalVerificationHandler, 
  extractVerificationErrorData,
} from '@/lib/verification-handler';

export interface VerificationStatus {
  isEmailVerified: boolean;
  isDataVerified: boolean;
  isIdVerified: boolean;
  isLoading: boolean;
}

export type VerificationRequirement = 'data' | 'id';

interface VerificationContextType {
  /**
   * Current verification status
   */
  verificationStatus: VerificationStatus;
  
  /**
   * Refresh verification status from the server
   */
  refreshVerificationStatus: () => Promise<void>;
  
  /**
   * Check if user can perform an action that requires verification.
   * Returns true if allowed, false if blocked.
   * Shows appropriate modal/toast if blocked.
   * 
   * @param requirement - 'data' for most actions, 'id' for redemption
   */
  checkVerificationAndPrompt: (requirement?: VerificationRequirement) => boolean;
  
  /**
   * Check if user meets verification requirements without showing prompts.
   * Use this for conditional rendering (e.g., disabling buttons).
   */
  isVerified: (requirement?: VerificationRequirement) => boolean;
  
  /**
   * Handle a potential verification error from API.
   * Returns true if the error was a verification error and was handled.
   */
  handleVerificationError: (error: any) => boolean;
}

const VerificationContext = createContext<VerificationContextType | undefined>(undefined);

export const useVerification = () => {
  const context = useContext(VerificationContext);
  if (context === undefined) {
    throw new Error('useVerification must be used within a VerificationProvider');
  }
  return context;
};

export const VerificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setShowJoiningBonusModal } = useJoiningBonus();
  
  // Use TanStack Query for verification status with WebSocket invalidation
  const { data: queryStatus, isLoading, refetch } = useVerificationStatusQuery();
  
  // Debounce to prevent multiple toasts/modals from rapid API failures
  const lastHandledRef = useRef<number>(0);
  const DEBOUNCE_MS = 2000;

  // Build verification status object for consumers
  const verificationStatus: VerificationStatus = {
    isEmailVerified: user?.email_verified || false,
    isDataVerified: queryStatus?.dataVerificationStatus === 'completed',
    isIdVerified: queryStatus?.idVerificationStatus === 'completed',
    isLoading,
  };

  // Refresh verification status (calls TanStack Query refetch)
  const refreshVerificationStatus = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.error('Error refreshing verification status:', error);
    }
  }, [refetch]);

  // Check if user meets verification requirements (no prompts)
  const isVerified = useCallback((requirement: VerificationRequirement = 'data'): boolean => {
    const { isEmailVerified, isDataVerified, isIdVerified } = verificationStatus;
    
    if (requirement === 'id') {
      return isEmailVerified && isDataVerified && isIdVerified;
    }
    
    // Default: data verification
    return isEmailVerified && isDataVerified;
  }, [verificationStatus]);

  // Check verification and show prompt if not verified
  const checkVerificationAndPrompt = useCallback((requirement: VerificationRequirement = 'data'): boolean => {
    // If user is not authenticated, allow action (they'll be prompted to login elsewhere)
    if (!user) {
      return true;
    }
    
    const isOnProfilePage = location.pathname === '/profile';
    
    // If loading, allow the action (backend will handle it)
    if (verificationStatus.isLoading) {
      return true;
    }
    
    // Check if user meets requirements
    if (isVerified(requirement)) {
      return true;
    }
    
    // Debounce prompts
    const now = Date.now();
    if (now - lastHandledRef.current < DEBOUNCE_MS) {
      return false;
    }
    lastHandledRef.current = now;
    
    if (requirement === 'id') {
      // ID verification required (for redemption)
      toast({
        title: 'ID Verification Required',
        description: 'Please complete ID verification to redeem prizes.',
        variant: 'destructive',
      });
      
      if (!isOnProfilePage) {
        navigate('/profile', { 
          state: { 
            scrollToVerification: true,
            showIdVerification: true 
          } 
        });
      }
    } else {
      // Data verification required
      toast({
        title: 'Verification Required',
        description: 'Please complete identity verification to participate.',
        variant: 'destructive',
      });
      
      if (!isOnProfilePage) {
        setShowJoiningBonusModal(true);
      }
    }
    
    return false;
  }, [user, verificationStatus, isVerified, location.pathname, navigate, setShowJoiningBonusModal]);

  // Handle API verification errors
  const handleVerificationError = useCallback((error: any): boolean => {
    const verificationError = extractVerificationErrorData(error);
    
    if (!verificationError) {
      return false;
    }

    const now = Date.now();
    if (now - lastHandledRef.current < DEBOUNCE_MS) {
      return true;
    }
    lastHandledRef.current = now;

    const isOnProfilePage = location.pathname === '/profile';

    if (verificationError.code === 'DATA_VERIFICATION_REQUIRED') {
      toast({
        title: 'Verification Required',
        description: 'Please complete identity verification to participate.',
        variant: 'destructive',
      });

      if (!isOnProfilePage) {
        setShowJoiningBonusModal(true);
      }
      
      // Invalidate cache to refetch status
      refetch();
      
      return true;
    }

    if (verificationError.code === 'ID_VERIFICATION_REQUIRED') {
      toast({
        title: 'ID Verification Required',
        description: 'Please complete ID verification to redeem prizes.',
        variant: 'destructive',
      });

      if (!isOnProfilePage) {
        navigate('/profile', { 
          state: { 
            scrollToVerification: true,
            showIdVerification: true 
          } 
        });
      }
      
      // Invalidate cache to refetch status
      refetch();
      
      return true;
    }

    return false;
  }, [location.pathname, navigate, setShowJoiningBonusModal, refetch]);

  // Register the global handler when the context mounts
  useEffect(() => {
    setGlobalVerificationHandler(handleVerificationError);
    return () => {
      setGlobalVerificationHandler(null);
    };
  }, [handleVerificationError]);

  return (
    <VerificationContext.Provider value={{ 
      verificationStatus,
      refreshVerificationStatus,
      checkVerificationAndPrompt,
      isVerified,
      handleVerificationError,
    }}>
      {children}
    </VerificationContext.Provider>
  );
};
